import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api, formatApiError } from "@/api";
import { useAuth } from "@/auth";
import { useLang } from "@/i18n";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Plus, Trash2, Save, ArrowLeft } from "lucide-react";

const newMember = (idx) => ({
  member_id: `M${idx}`, name: "", relation: idx === 1 ? "Self" : "Son", gender: "Male",
  dob: "", marital_status: "Unmarried", education: "Primary School", profession: "Student", caste: "OC",
});

const EMPTY = {
  village: "K. Gopalapuram", ward: "Ward 1", street: "", address: "",
  own_house: true, house_type: "Pucca", patta_issued: false, patta_number: "",
  needs_new_house_patta: false, owns_agri_land: false, needs_borewell: false,
  members: [newMember(1)], land_parcels: [], crops: [], borewells: [],
};

function YesNo({ value, onChange, testid }) {
  const { t } = useLang();
  return (
    <Select value={value ? "yes" : "no"} onValueChange={(v) => onChange(v === "yes")}>
      <SelectTrigger data-testid={testid} className="h-9"><SelectValue /></SelectTrigger>
      <SelectContent>
        <SelectItem value="yes">{t("yes")}</SelectItem>
        <SelectItem value="no">{t("no")}</SelectItem>
      </SelectContent>
    </Select>
  );
}

function MasterSelect({ value, onChange, options, testid }) {
  return (
    <Select value={value || ""} onValueChange={onChange}>
      <SelectTrigger data-testid={testid} className="h-9"><SelectValue /></SelectTrigger>
      <SelectContent>
        {options.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}
      </SelectContent>
    </Select>
  );
}

function Field({ label, children, className = "" }) {
  return (
    <div className={`space-y-1.5 ${className}`}>
      <Label className="text-xs text-slate-500">{label}</Label>
      {children}
    </div>
  );
}

export default function FamilyForm() {
  const { familyId } = useParams();
  const isEdit = !!familyId;
  const { t } = useLang();
  const { scope } = useAuth();
  const navigate = useNavigate();
  const [meta, setMeta] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/meta").then(({ data }) => setMeta(data)).catch(() => {});
    if (isEdit) {
      api.get(`/families/${familyId}`, { params: { scope } })
        .then(({ data }) => setForm({ ...EMPTY, ...data }))
        .catch((err) => {
          toast.error(formatApiError(err));
          navigate("/families");
        });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [familyId, scope]);

  if (!meta) return null;

  const set = (key, value) => setForm((f) => ({ ...f, [key]: value }));
  const setArr = (key, idx, field, value) =>
    setForm((f) => {
      const arr = [...f[key]];
      arr[idx] = { ...arr[idx], [field]: value };
      return { ...f, [key]: arr };
    });
  const addTo = (key, item) => setForm((f) => ({ ...f, [key]: [...f[key], item] }));
  const removeFrom = (key, idx) => setForm((f) => ({ ...f, [key]: f[key].filter((_, i) => i !== idx) }));

  const num = (v) => (v === "" ? 0 : Math.max(0, parseInt(v, 10) || 0));

  const submit = async (e) => {
    e.preventDefault();
    const members = form.members.filter((m) => m.name.trim());
    if (members.length === 0 || members.some((m) => !m.dob)) {
      toast.error(t("validation_members"));
      return;
    }
    if (members.filter((m) => m.relation === "Self").length !== 1) {
      toast.error(t("validation_head"));
      return;
    }
    const payload = {
      ...form,
      members: members.map((m, i) => ({ ...m, member_id: `M${i + 1}` })),
      patta_number: form.patta_issued ? form.patta_number : null,
      house_type: form.own_house ? form.house_type : null,
      land_parcels: form.owns_agri_land ? form.land_parcels.filter((p) => p.survey_number) : [],
      crops: form.owns_agri_land ? form.crops : [],
      borewells: form.borewells.map((b, i) => ({
        ...b, borewell_id: `BW${i + 1}`,
        service_number: b.electricity_connection ? b.service_number : null,
        connection_date: b.electricity_connection ? b.connection_date : null,
      })),
    };
    setSaving(true);
    try {
      const { data } = isEdit
        ? await api.put(`/families/${familyId}`, payload, { params: { scope } })
        : await api.post("/families", payload, { params: { scope } });
      toast.success(t("saved"));
      navigate(`/families/${data.family_id}`);
    } catch (err) {
      toast.error(t("save_failed"), { description: formatApiError(err) });
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={submit} className="space-y-5 max-w-6xl" data-testid="family-form">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-4">
          <Button type="button" data-testid="form-back-btn" variant="outline" size="icon" onClick={() => navigate(-1)}>
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <h1 className="text-2xl md:text-3xl tracking-tight font-semibold text-graphite" data-testid="form-title">
            {isEdit ? `${t("edit_family_title")} — ${familyId}` : t("add_family_title")}
          </h1>
        </div>
        <Button data-testid="save-family-btn" type="submit" disabled={saving} className="bg-brand-red hover:bg-brand-reddark text-white transition-colors duration-200">
          <Save className="h-4 w-4 mr-2" />{t("save")}
        </Button>
      </div>

      <Card className="border-slate-200">
        <CardHeader className="pb-3"><CardTitle className="text-lg">{t("basic_details")}</CardTitle></CardHeader>
        <CardContent className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <Field label={t("f_village")}><Input data-testid="input-village" value={form.village} disabled /></Field>
          <Field label={t("f_ward")}>
            <MasterSelect value={form.ward} onChange={(v) => set("ward", v)} options={meta.wards} testid="input-ward" />
          </Field>
          <Field label={t("f_street")}><Input data-testid="input-street" value={form.street} onChange={(e) => set("street", e.target.value)} required /></Field>
          <Field label={t("f_address")}><Input data-testid="input-address" value={form.address} onChange={(e) => set("address", e.target.value)} /></Field>
        </CardContent>
      </Card>

      <Card className="border-slate-200">
        <CardHeader className="pb-3"><CardTitle className="text-lg">{t("housing_details")}</CardTitle></CardHeader>
        <CardContent className="grid sm:grid-cols-2 lg:grid-cols-5 gap-4">
          <Field label={t("f_own_house")}><YesNo value={form.own_house} onChange={(v) => set("own_house", v)} testid="input-own-house" /></Field>
          {form.own_house && (
            <Field label={t("f_house_type")}>
              <MasterSelect value={form.house_type} onChange={(v) => set("house_type", v)} options={meta.house_types} testid="input-house-type" />
            </Field>
          )}
          <Field label={t("f_patta_issued")}><YesNo value={form.patta_issued} onChange={(v) => set("patta_issued", v)} testid="input-patta-issued" /></Field>
          {form.patta_issued && (
            <Field label={t("f_patta_number")}><Input data-testid="input-patta-number" value={form.patta_number || ""} onChange={(e) => set("patta_number", e.target.value)} /></Field>
          )}
          <Field label={t("f_needs_patta")}><YesNo value={form.needs_new_house_patta} onChange={(v) => set("needs_new_house_patta", v)} testid="input-needs-patta" /></Field>
        </CardContent>
      </Card>

      <Card className="border-slate-200">
        <CardHeader className="pb-3 flex flex-row items-center justify-between">
          <CardTitle className="text-lg">{t("family_members")}</CardTitle>
          <Button type="button" data-testid="add-member-btn" variant="outline" size="sm"
            onClick={() => addTo("members", newMember(form.members.length + 1))}>
            <Plus className="h-4 w-4 mr-1" />{t("add_member")}
          </Button>
        </CardHeader>
        <CardContent className="space-y-4">
          {form.members.map((m, i) => (
            <div key={i} className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-9 gap-3 items-end border border-slate-100 rounded-md p-3 bg-slate-50/50" data-testid={`member-form-${i}`}>
              <Field label={t("m_name")} className="col-span-2"><Input data-testid={`member-name-${i}`} value={m.name} onChange={(e) => setArr("members", i, "name", e.target.value)} /></Field>
              <Field label={t("m_relation")}><MasterSelect value={m.relation} onChange={(v) => setArr("members", i, "relation", v)} options={meta.relations} testid={`member-relation-${i}`} /></Field>
              <Field label={t("m_gender")}><MasterSelect value={m.gender} onChange={(v) => setArr("members", i, "gender", v)} options={meta.genders} testid={`member-gender-${i}`} /></Field>
              <Field label={t("m_dob")}><Input data-testid={`member-dob-${i}`} type="date" value={m.dob || ""} onChange={(e) => setArr("members", i, "dob", e.target.value)} /></Field>
              <Field label={t("m_marital")}><MasterSelect value={m.marital_status} onChange={(v) => setArr("members", i, "marital_status", v)} options={meta.marital_statuses} testid={`member-marital-${i}`} /></Field>
              <Field label={t("m_education")}><MasterSelect value={m.education} onChange={(v) => setArr("members", i, "education", v)} options={meta.education} testid={`member-education-${i}`} /></Field>
              <Field label={t("m_profession")}><MasterSelect value={m.profession} onChange={(v) => setArr("members", i, "profession", v)} options={meta.professions} testid={`member-profession-${i}`} /></Field>
              <div className="flex items-end gap-2">
                <Field label={t("m_caste")} className="flex-1"><MasterSelect value={m.caste} onChange={(v) => setArr("members", i, "caste", v)} options={meta.castes} testid={`member-caste-${i}`} /></Field>
                {form.members.length > 1 && (
                  <Button type="button" data-testid={`member-remove-${i}`} variant="ghost" size="icon" className="h-9 w-9 shrink-0" onClick={() => removeFrom("members", i)}>
                    <Trash2 className="h-4 w-4 text-brand-red" />
                  </Button>
                )}
              </div>
            </div>
          ))}
          <p className="text-[11px] text-slate-400">{t("caste_disclaimer")}</p>
        </CardContent>
      </Card>

      <Card className="border-slate-200">
        <CardHeader className="pb-3 flex flex-row items-center justify-between">
          <CardTitle className="text-lg">{t("agriculture_details")}</CardTitle>
          {form.owns_agri_land && (
            <Button type="button" data-testid="add-parcel-btn" variant="outline" size="sm"
              onClick={() => addTo("land_parcels", { parcel_id: `P${form.land_parcels.length + 1}`, survey_number: "", acres: 0, cents: 0, land_type: "Irrigated" })}>
              <Plus className="h-4 w-4 mr-1" />{t("add_parcel")}
            </Button>
          )}
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid sm:grid-cols-3 gap-4 max-w-2xl">
            <Field label={t("f_owns_land")}><YesNo value={form.owns_agri_land} onChange={(v) => set("owns_agri_land", v)} testid="input-owns-land" /></Field>
            <Field label={t("f_needs_borewell")}><YesNo value={form.needs_borewell} onChange={(v) => set("needs_borewell", v)} testid="input-needs-borewell" /></Field>
          </div>
          {form.owns_agri_land && form.land_parcels.map((p, i) => (
            <div key={i} className="grid grid-cols-2 md:grid-cols-6 gap-3 items-end border border-slate-100 rounded-md p-3 bg-slate-50/50" data-testid={`parcel-form-${i}`}>
              <Field label={t("p_survey")} className="col-span-2"><Input data-testid={`parcel-survey-${i}`} value={p.survey_number} onChange={(e) => setArr("land_parcels", i, "survey_number", e.target.value)} /></Field>
              <Field label={t("p_acres")}><Input data-testid={`parcel-acres-${i}`} type="number" min="0" value={p.acres} onChange={(e) => setArr("land_parcels", i, "acres", num(e.target.value))} /></Field>
              <Field label={t("p_cents")}><Input data-testid={`parcel-cents-${i}`} type="number" min="0" max="99" value={p.cents} onChange={(e) => setArr("land_parcels", i, "cents", num(e.target.value))} /></Field>
              <Field label={t("p_land_type")}><MasterSelect value={p.land_type} onChange={(v) => setArr("land_parcels", i, "land_type", v)} options={meta.land_types} testid={`parcel-type-${i}`} /></Field>
              <Button type="button" data-testid={`parcel-remove-${i}`} variant="ghost" size="icon" className="h-9 w-9" onClick={() => removeFrom("land_parcels", i)}>
                <Trash2 className="h-4 w-4 text-brand-red" />
              </Button>
            </div>
          ))}
        </CardContent>
      </Card>

      {form.owns_agri_land && (
        <Card className="border-slate-200">
          <CardHeader className="pb-3 flex flex-row items-center justify-between">
            <CardTitle className="text-lg">{t("crops_details")}</CardTitle>
            <Button type="button" data-testid="add-crop-btn" variant="outline" size="sm"
              onClick={() => addTo("crops", { crop: "Paddy", season: "Kharif", year: "2025-2026", acres: 0, cents: 0, irrigation_source: "Borewell" })}>
              <Plus className="h-4 w-4 mr-1" />{t("add_crop")}
            </Button>
          </CardHeader>
          <CardContent className="space-y-4">
            {form.crops.map((c, i) => (
              <div key={i} className="grid grid-cols-2 md:grid-cols-7 gap-3 items-end border border-slate-100 rounded-md p-3 bg-slate-50/50" data-testid={`crop-form-${i}`}>
                <Field label={t("c_crop")}><MasterSelect value={c.crop} onChange={(v) => setArr("crops", i, "crop", v)} options={meta.crops} testid={`crop-crop-${i}`} /></Field>
                <Field label={t("c_season")}><MasterSelect value={c.season} onChange={(v) => setArr("crops", i, "season", v)} options={meta.seasons} testid={`crop-season-${i}`} /></Field>
                <Field label={t("c_year")}><Input data-testid={`crop-year-${i}`} value={c.year} onChange={(e) => setArr("crops", i, "year", e.target.value)} /></Field>
                <Field label={t("p_acres")}><Input data-testid={`crop-acres-${i}`} type="number" min="0" value={c.acres} onChange={(e) => setArr("crops", i, "acres", num(e.target.value))} /></Field>
                <Field label={t("p_cents")}><Input data-testid={`crop-cents-${i}`} type="number" min="0" max="99" value={c.cents} onChange={(e) => setArr("crops", i, "cents", num(e.target.value))} /></Field>
                <Field label={t("c_irrigation")}><MasterSelect value={c.irrigation_source} onChange={(v) => setArr("crops", i, "irrigation_source", v)} options={meta.irrigation_sources} testid={`crop-irrigation-${i}`} /></Field>
                <Button type="button" data-testid={`crop-remove-${i}`} variant="ghost" size="icon" className="h-9 w-9" onClick={() => removeFrom("crops", i)}>
                  <Trash2 className="h-4 w-4 text-brand-red" />
                </Button>
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      <Card className="border-slate-200">
        <CardHeader className="pb-3 flex flex-row items-center justify-between">
          <CardTitle className="text-lg">{t("borewells_details")}</CardTitle>
          <Button type="button" data-testid="add-borewell-btn" variant="outline" size="sm"
            onClick={() => addTo("borewells", { borewell_id: `BW${form.borewells.length + 1}`, borewell_type: "Private", status: "Working", depth_ft: 200, electricity_connection: false, service_number: "", connection_date: "" })}>
            <Plus className="h-4 w-4 mr-1" />{t("add_borewell")}
          </Button>
        </CardHeader>
        <CardContent className="space-y-4">
          {form.borewells.map((b, i) => (
            <div key={i} className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-3 items-end border border-slate-100 rounded-md p-3 bg-slate-50/50" data-testid={`borewell-form-${i}`}>
              <Field label={t("b_type")}><MasterSelect value={b.borewell_type} onChange={(v) => setArr("borewells", i, "borewell_type", v)} options={meta.borewell_types} testid={`bw-type-${i}`} /></Field>
              <Field label={t("b_status")}><MasterSelect value={b.status} onChange={(v) => setArr("borewells", i, "status", v)} options={meta.borewell_statuses} testid={`bw-status-${i}`} /></Field>
              <Field label={t("b_depth")}><Input data-testid={`bw-depth-${i}`} type="number" min="0" value={b.depth_ft} onChange={(e) => setArr("borewells", i, "depth_ft", num(e.target.value))} /></Field>
              <Field label={t("b_electricity")}><YesNo value={b.electricity_connection} onChange={(v) => setArr("borewells", i, "electricity_connection", v)} testid={`bw-elec-${i}`} /></Field>
              {b.electricity_connection && (
                <>
                  <Field label={t("b_service_no")} className="col-span-2"><Input data-testid={`bw-service-${i}`} value={b.service_number || ""} onChange={(e) => setArr("borewells", i, "service_number", e.target.value)} /></Field>
                  <Field label={t("b_conn_date")}><Input data-testid={`bw-conndate-${i}`} type="date" value={b.connection_date || ""} onChange={(e) => setArr("borewells", i, "connection_date", e.target.value)} /></Field>
                </>
              )}
              <Button type="button" data-testid={`bw-remove-${i}`} variant="ghost" size="icon" className="h-9 w-9" onClick={() => removeFrom("borewells", i)}>
                <Trash2 className="h-4 w-4 text-brand-red" />
              </Button>
            </div>
          ))}
          {form.borewells.length === 0 && <p className="text-sm text-slate-400">{t("na")}</p>}
        </CardContent>
      </Card>
    </form>
  );
}
